import React, { useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { AuthContextType } from 'contexts';
import { isFullAccessUser } from 'utils';

import { Listing } from 'api/ListingController/getListing';

import Alert from '../../Alert';
import Icon from '../../Icon';
import theme, { ColorNameType } from '../../theme';

interface PendingListingWarningProps {
  listing: Listing & { isUnclaimed?: boolean };
  auth: AuthContextType['auth'];
  external?: boolean;
  isInternalAdmin?: boolean;
  $rootScope: {
    hasFeature: AuthContextType['hasFeature'];
  };
}

const PendingListingWarning: React.FC<PendingListingWarningProps> = ({
  listing,
  auth,
  external,
  isInternalAdmin,
  $rootScope: { hasFeature },
}) => {
  const data = {
    rejected: {
      message: (
        <StyledWarningText alertType="error">
          This listing agreement / paperwork has been rejected. Replace the
          document and submit for review.{' '}
          <a href={`/#/listing/${listing.id}/documents`}>Manage Documents</a>
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="antdCloseCircleFilled"
          color={theme.red6 as ColorNameType}
          width={16}
          offset={[0, -1]}
        />
      ),
      alertType: 'error',
    },
    unconfigured: {
      message: (
        <StyledWarningText>
          Showing settings are not configured.{' '}
          <a href={`/#/listing/${listing.id}/appointments/config`}>
            Configure Showings
          </a>
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="exclamationTriangleFilled"
          color={theme.blue6 as ColorNameType}
          offset={[0, -3]}
        />
      ),
      alertType: 'info',
    },
    pendingUnconfigured: {
      message: (
        <StyledWarningText>
          This listing is pending administrator approval. In the meantime,
          please configure your showing settings.{' '}
          <a href={`/#/listing/${listing.id}/appointments/config`}>
            Configure Showings
          </a>
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="exclamation"
          color={theme.blue6 as ColorNameType}
          width={20}
          offset={[0, -3]}
        />
      ),
      alertType: 'info',
    },
    unclaimed: {
      message: (
        <StyledWarningText>
          Actions cannot be taken on this listing because it is not represented
          by BrokerBay. Please refer to your MLS to book via the correct showing
          service.
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="exclamation"
          color={theme.blue6 as ColorNameType}
          offset={[0, -3]}
        />
      ),
      alertType: 'info',
    },
    pendingConfigured: {
      message: (
        <StyledWarningText>
          This listing is pending administrator approval.
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="exclamation"
          color={theme.gold6 as ColorNameType}
          width={20}
          offset={[0, -3]}
        />
      ),
      alertType: 'warning',
    },
    uploaded: {
      message: (
        <StyledWarningText>
          This draft listing has not been submitted for administrator approval
          yet.{' '}
          {!auth.isAdmin && (
            <a href={`/#/listing/${listing.id}/edit?submit=true`}>
              Submit for Approval
            </a>
          )}
        </StyledWarningText>
      ),
      icon: (
        <Icon
          type="exclamation"
          color={theme.gold6 as ColorNameType}
          width={20}
          offset={[0, -3]}
        />
      ),
      alertType: 'warning',
    },
  } as const;

  const filteredAgents = useMemo(
    () => listing.reps.filter(rep => rep.id !== auth.id).map(rep => rep.id),
    [listing.reps, auth.id],
  );

  const getKey = (): keyof typeof data => {
    if (external) {
      if (!isInternalAdmin && listing.isUnclaimed) return 'unclaimed';
      return null;
    }

    if (!hasFeature('CREATE_LISTING')) {
      if (!listing.configured) return 'unconfigured';
      return null;
    }

    if (!isFullAccessUser(auth) && !isInternalAdmin && listing.isUnclaimed) {
      return 'unclaimed';
    }

    switch (listing.documentStatus) {
      case 'uploaded':
        return 'uploaded';
      case 'pending':
        if (listing.configured) return 'pendingConfigured';
        return 'pendingUnconfigured';
      case 'rejected':
        return 'rejected';
      case 'accepted':
        if (listing.configured) return null;
        return 'unconfigured';
      default:
        return null;
    }
  };

  const warningData = data[getKey()];
  if (!warningData) return null;

  const { message, alertType, icon } = warningData;

  return (
    <StyledWarningContainer>
      <Alert
        closable={false}
        showIcon
        icon={icon}
        message={message}
        type={alertType}
      />
    </StyledWarningContainer>
  );
};

export default PendingListingWarning;

const StyledWarningContainer = styled.div`
  margin-bottom: 15px;

  .ant-alert {
    padding: 10px 15px 10px 20px;
  }

  .ant-alert-info {
    background-color: ${theme.blue1};
  }

  .ant-alert-warning {
    background-color: ${theme.gold1};
    border-color: ${theme.gold4};

    .ant-alert-icon {
      color: ${theme.gold6};
    }
  }

  .ant-alert-error .ant-alert-icon {
    color: ${theme.red6};
  }
`;

const StyledWarningText = styled.span<{ alertType?: string }>`
  font-size: 14px;
  color: ${theme.gray8};

  a {
    color: ${({ alertType }) =>
      alertType === 'error' ? theme.red6 : theme.blue6};
  }
`;
